/**
 * Shutter — two pictures in one frame, with a bar you drag between them.
 *
 * The left picture is laid over the right and clipped at the bar; dragging
 * (mouse or finger), or the arrow keys with the bar focused, moves the cut.
 * The frame keeps `touch-action: pan-y`, so a vertical swipe that starts on
 * the picture still scrolls the page — only sideways movement is ours.
 *
 * Sizing comes from the parent: the shutter fills whatever box it is put in.
 */

import React, { useCallback, useRef, useState } from 'react';

interface Side {
  src: string;
  alt: string;
  /** Short name shown in the corner of that side. */
  label: string;
}

interface ElfShutterProps {
  left: Side;
  right: Side;
  /** `object-position` for both pictures, so their crops stay registered. */
  focus?: string;
  /** Where the bar starts, 0–100. */
  start?: number;
}

const clamp = (v: number) => Math.max(0, Math.min(100, v));

export const ElfShutter: React.FC<ElfShutterProps> = ({ left, right, focus = '50% 50%', start = 50 }) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [cut, setCut] = useState(start);

  const cutAt = useCallback((clientX: number) => {
    const frame = frameRef.current;
    if (!frame) return;
    const r = frame.getBoundingClientRect();
    setCut(clamp(((clientX - r.left) / r.width) * 100));
  }, []);

  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    cutAt(e.clientX);
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragging.current) cutAt(e.clientX);
  };
  const onUp = () => {
    dragging.current = false;
  };

  const onKey = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 4;
    if (e.key === 'ArrowLeft') setCut((c) => clamp(c - step));
    else if (e.key === 'ArrowRight') setCut((c) => clamp(c + step));
    else if (e.key === 'Home') setCut(0);
    else if (e.key === 'End') setCut(100);
    else return;
    e.preventDefault();
  };

  return (
    <div
      ref={frameRef}
      className="shutter"
      style={{ '--cut': `${cut}%` } as React.CSSProperties}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <img className="shutter__img" src={right.src} alt={right.alt} style={{ objectPosition: focus }} draggable={false} />
      <img
        className="shutter__img shutter__img--over"
        src={left.src}
        alt={left.alt}
        style={{ objectPosition: focus }}
        draggable={false}
      />
      <span className="shutter__tag shutter__tag--l" data-hide={cut < 18 ? 'true' : undefined}>
        {left.label}
      </span>
      <span className="shutter__tag shutter__tag--r" data-hide={cut > 82 ? 'true' : undefined}>
        {right.label}
      </span>
      <div
        className="shutter__bar"
        role="slider"
        tabIndex={0}
        aria-label={`Slide between ${left.label} and ${right.label}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(cut)}
        aria-valuetext={`${Math.round(cut)}% ${left.label}`}
        onKeyDown={onKey}
      >
        <span className="shutter__grip" aria-hidden="true">
          ‹ ›
        </span>
      </div>
    </div>
  );
};

export default ElfShutter;
